import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { AgGridAngular } from 'ag-grid-angular';
import {
  ColDef,
  RowSelectionOptions,
  GridApi,
  GridReadyEvent,
  CellEditingStoppedEvent,
  GridOptions,
  IRowNode,
  ICellRendererParams,
  ICellEditorParams,
} from 'ag-grid-community'; // Column Definition Type Interface
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Subscription } from 'rxjs';
import {
  catchError,
  filter,
  map,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs/operators';
import { CellEditorComponent } from '~app/components/shared/cell-editor/cell-editor.component';
import { CellRendererComponent } from '~app/components/shared/cell-renderer/cell-renderer.component';
import { PersonNameCellRendererComponent } from '~app/components/shared/cell-renderer/person-name-cell-renderer.component';
import { SaveBtnComponent } from '~app/components/shared/cell-renderer/save-btn-renderer.component';
import { SatPhoneValidator } from '~app/components/shared/forms/field/field.component';
import { NoRowsMessageComponent } from '~app/components/shared/no-rows-message.component';
import { PinnedCellRendererComponent } from '~app/components/shared/pinned-cell-renderer/pinned-cell-renderer.component';
import { RowOptionsComponent } from '~app/components/views/trip/trip-page-v3/row-options/row-options.component';
import { CrewMemberMetaAttributes, PersonAttributes } from '~app/models';
import { TripService } from '~app/services';
import { AuthService } from '~app/services/auth.service';
import { RolePermissionService } from '~app/services/role-permission.service';
import { UserGroupService } from '~app/services/user-group.service';
import { FormsUtility } from '~app/utilities';
import { repeatLatestWhen } from '~app/utilities/repeat-latest-when.operator';
import { combineLatest } from 'rxjs';

@Component({
  selector: 'ciao-crew-member-grid',
  imports: [AgGridAngular],
  templateUrl: './crew-member-grid.component.html',
  styleUrl: './crew-member-grid.component.less',
})
export class CrewMemberGridComponent {
  constructor(
    private readonly tripService: TripService,
    private readonly toastr: ToastrService,
    private readonly userGroupService: UserGroupService,
    private readonly authService: AuthService,
    private readonly rolePermissionService: RolePermissionService
  ) {
    this.subscriptions.add(
      combineLatest([
        this.rolePermissionService.isGuest$,
        this.authService.currentUser$,
      ]).subscribe(([isGuest, currentUser]) => {
        this.lockedPersonId =
          isGuest && currentUser
            ? currentUser.personId || currentUser.person?.id || null
            : null;
      })
    );
  }

  private lockedPersonId: string | null = null;

  private isLockedRow = (data: CrewMemberMetaAttributes): boolean => {
    if (!this.lockedPersonId || !data) return false;
    return data.crewMember?.id === this.lockedPersonId;
  };

  /**
   * True when a person column references a deleted user: the row still has the
   * `<field>Id` but the joined person object is gone. Such cells render a
   * greyed-out "Deleted user" placeholder and must not be editable, so trip
   * history can't be silently re-pointed at a live user.
   */
  private isDeletedPersonCell = (
    field: 'crewMember' | 'supervisor' | 'secondSupervisor',
    data: CrewMemberMetaAttributes
  ): boolean => {
    if (!data) return false;
    return !data[field] && !!data[`${field}Id`];
  };
  @Input() tripId: string;
  @Input() rowData: CrewMemberMetaAttributes[];
  @Input() defaultColDef: ColDef;
  @Input() defaultRowSelection: RowSelectionOptions | 'single' | 'multiple';
  private _readOnly = false;
  /** When true (e.g. the trip is Closed) lock the grid: no cell editing, no
   * add-row, no row delete. The backend also rejects edits to closed trips. */
  @Input() set readOnly(value: boolean) {
    this._readOnly = !!value;
    this.applyReadOnly();
  }
  get readOnly(): boolean {
    return this._readOnly;
  }
  /** emit null if no errors.  emit _something_ (either array of errors, or just 'true') if there are errors */
  @Output() errors = new EventEmitter<any>();
  private subscriptions: Subscription = new Subscription();

  @Input() set userGroupId(value: string) {
    this.userGroupId$.next(value);
  }
  userGroupId$ = new BehaviorSubject<string>(null);

  readonly refreshDependentLists$ = new BehaviorSubject<void>(null);
  readonly crewMemberAndSupervisorList$ = this.userGroupId$.pipe(
    filter((x) => !!x),
    switchMap((userGroupId) =>
      this.userGroupService.getCrewMemberDropdownLists(userGroupId)
    )
  );
  readonly crewMemberOptions$ = this.crewMemberAndSupervisorList$.pipe(
    map((listByRoles) => listByRoles.crew_member),
    repeatLatestWhen(this.refreshDependentLists$),
    tap((crewmembers) =>
      crewmembers.forEach(
        (option) =>
          (option.disabled = this.supervisorIds.includes(option.value?.id))
      )
    ),
    // need to use shareReplay(1) so that obs does not die after first edit
    shareReplay(1)
  );

  readonly supervisorOptions$ = this.crewMemberAndSupervisorList$.pipe(
    map((listByRoles) => listByRoles.supervisor),
    repeatLatestWhen(this.refreshDependentLists$),
    tap((supervisors) =>
      supervisors.forEach(
        (option) =>
          (option.disabled = this.crewMemberIds.includes(option.value?.id))
      )
    ),
    // need to use shareReplay(1) so that obs does not die after first edit
    shareReplay(1)
  );

  readonly secondSupervisorOptions$ = this.crewMemberAndSupervisorList$.pipe(
    map((listByRoles) => [
      { value: null, label: ' ', disabled: false },
      ...listByRoles.additional_poc,
    ]),
    repeatLatestWhen(this.refreshDependentLists$),
    tap((supervisors) =>
      supervisors.forEach(
        (option) =>
          (option.disabled = this.crewMemberIds.includes(option.value?.id))
      )
    ),
    // need to use shareReplay(1) so that obs does not die after first edit
    shareReplay(1)
  );

  gridApi: GridApi;
  inputRow: Partial<CrewMemberMetaAttributes> = {};
  pinnedTopRowData = [this.inputRow];
  allowSaveOnPinned: boolean = false;

  get crewMemberIds() {
    // NOTE: This runs waaaay too often.  But can't be avoided in 2.0.0
    const ids =
      this.rowData
        ?.map((meta) => meta.crewMember?.id)
        // Filter to only the first instance of each value
        .filter((value, index, arr) => arr.indexOf(value) === index) || [];
    return ids;
  }
  get supervisorIds() {
    // NOTE: This runs waaaay too often.  But can't be avoided in 2.0.0
    const ids = [
      ...(this.rowData?.map((meta) => meta.supervisor?.id) || []),
      ...(this.rowData?.map((meta) => meta.secondSupervisor?.id) || []),
    ]
      // Filter to only the first instance of each value
      .filter((value, index, arr) => arr.indexOf(value) === index);
    return ids;
  }

  readonly testFormGroup = new FormGroup(
    {
      id: new FormControl(''),
      crewMember: new FormControl<PersonAttributes>(null, [
        Validators.required,
        (control: FormControl<PersonAttributes>) => {
          if (this.supervisorIds.includes(control.value?.id)) {
            return {
              sameCrewSupeOnTrip:
                'Crew Member cannot be a Point of Contact on the same trip.',
            };
          }
          return null;
        },
      ]),
      supervisor: new FormControl<PersonAttributes>(null, [
        Validators.required,
        (control: FormControl<PersonAttributes>) => {
          if (this.crewMemberIds.includes(control.value?.id)) {
            return {
              sameCrewSupeOnTrip:
                'Point Of Contact cannot be a Crew Member on the same trip.',
            };
          }
          return null;
        },
      ]),
      secondSupervisor: new FormControl<PersonAttributes>(null, [
        (control: FormControl<PersonAttributes>) => {
          if (this.crewMemberIds.includes(control.value?.id)) {
            return {
              sameCrewSupeOnTrip:
                'Point Of Contact cannot be a Crew Member on the same trip.',
            };
          }
        },
      ]),
      satPhone: new FormControl('', [SatPhoneValidator]),
      satLocatorId: new FormControl(''),
      notes: new FormControl(''),
    },
    [FormsUtility.combineChildValidation]
  );

  crewColDefs: ColDef[] = [
    {
      field: 'id',
      hide: true,
    },
    {
      headerName: 'Crew Member (at least one required)',
      field: 'crewMember',
      editable: (p) =>
        !this.isLockedRow(p.data) &&
        !this.isDeletedPersonCell('crewMember', p.data),
      cellDataType: 'person',
      flex: 2,
      minWidth: 250,
      headerComponentParams: {
        template: `<div class="ag-cell-label-container" role="presentation">
        <span data-ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>
        <span data-ref="eFilterButton" class="ag-header-icon ag-header-cell-filter-button"></span>
        <div data-ref="eLabel" class="ag-header-cell-label" role="presentation">
            <span data-ref="eSortOrder" class="ag-header-icon ag-sort-order ag-hidden"></span>
            <span data-ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon ag-hidden"></span>
            <span data-ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon ag-hidden"></span>
            <span data-ref="eSortMixed" class="ag-header-icon ag-sort-mixed-icon ag-hidden"></span>
            <span data-ref="eSortNone" class="ag-header-icon ag-sort-none-icon ag-hidden"></span>
            <span data-ref="eText" class="ag-header-cell-text" role="columnheader"></span>
            <p class="required">*</p>
            <span data-ref="eFilter" class="ag-header-icon ag-filter-icon"></span>
        </div>
    </div>`,
      },
      cellEditor: CellEditorComponent,
      cellEditorParams: {
        fieldType: 'combobox',
        commitOnSelect: true,
        options$: this.crewMemberOptions$,
        placeholder: 'Select Crew Member (*Required to save)',
        validators: [this.testFormGroup.controls.crewMember.validator],
      },
      cellRendererSelector: (p: ICellRendererParams) => this.getCellRenderer(p),
      cellRendererParams: {
        fieldType: 'dropdown',
        placeholder: 'Select Crew Member (*Required to save)',
        validators: [Validators.required],
      },
      comparator: (a, b) => {
        const displayNameA = a ? String(a?.displayName) : '';
        const displayNameB = b ? String(b?.displayName) : '';
        return displayNameA.localeCompare(displayNameB);
      },
      valueGetter: (params) => {
        let displayName = params.data?.crewMember?.displayName;
        if (params.data?.crewMember?.defaultPhone) {
          displayName += `\xa0\xa0${params.data?.crewMember?.defaultPhone?.phoneNumber}`;
        }
        return displayName;
      },
      wrapText: true,
    },
    {
      headerName: 'Point of Contact (POC)',
      field: 'supervisor',
      editable: (p) => !this.isDeletedPersonCell('supervisor', p.data),
      cellDataType: 'person',
      flex: 2,
      minWidth: 250,
      headerComponentParams: {
        template: `<div class="ag-cell-label-container" role="presentation">
        <span data-ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>
        <span data-ref="eFilterButton" class="ag-header-icon ag-header-cell-filter-button"></span>
        <div data-ref="eLabel" class="ag-header-cell-label" role="presentation">
            <span data-ref="eSortOrder" class="ag-header-icon ag-sort-order ag-hidden"></span>
            <span data-ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon ag-hidden"></span>
            <span data-ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon ag-hidden"></span>
            <span data-ref="eSortMixed" class="ag-header-icon ag-sort-mixed-icon ag-hidden"></span>
            <span data-ref="eSortNone" class="ag-header-icon ag-sort-none-icon ag-hidden"></span>
            <span data-ref="eText" class="ag-header-cell-text" role="columnheader"></span>
            <p class="required">*</p>
            <span data-ref="eFilter" class="ag-header-icon ag-filter-icon"></span>
        </div>
    </div>`,
      },
      cellEditor: CellEditorComponent,
      cellEditorParams: {
        fieldType: 'combobox',
        commitOnSelect: true,
        placeholder: 'Select Supervisor/Admin/Dispatcher',
        options$: this.supervisorOptions$,
        validators: [this.testFormGroup.controls.supervisor.validator],
      },
      cellRendererSelector: (p: ICellRendererParams) => this.getCellRenderer(p),
      cellRendererParams: {
        fieldType: 'dropdown',
        placeholder: 'Select Supervisor/Admin/Dispatcher',
        validators: [Validators.required],
      },
      // needed to fix sort when using complex objects
      comparator: (a, b) => {
        const displayNameA = a ? String(a?.displayName) : '';
        const displayNameB = b ? String(b?.displayName) : '';
        return displayNameA.localeCompare(displayNameB);
      },
      valueGetter: (params) => {
        let displayName = params.data?.supervisor?.displayName;
        if (params.data?.supervisor?.defaultPhone) {
          displayName += `\xa0\xa0${params.data?.supervisor?.defaultPhone?.phoneNumber}`;
        }
        return displayName;
      },
      wrapText: true,
    },
    {
      headerName: 'Additional POC',
      field: 'secondSupervisor',
      editable: (p) => !this.isDeletedPersonCell('secondSupervisor', p.data),
      cellDataType: 'person',
      flex: 2,
      minWidth: 250,
      cellEditor: CellEditorComponent,
      cellEditorParams: {
        fieldType: 'combobox',
        commitOnSelect: true,
        placeholder: 'Select a Team Member',
        options$: this.secondSupervisorOptions$,
      },
      cellRendererSelector: (p: ICellRendererParams) => this.getCellRenderer(p),
      cellRendererParams: {
        fieldType: 'dropdown',
        placeholder: 'Select a Team Member',
      },
      // needed to fix sort when using complex objects
      comparator: (a, b) => {
        const displayNameA = a ? String(a?.displayName) : '';
        const displayNameB = b ? String(b?.displayName) : '';
        return displayNameA.localeCompare(displayNameB);
      },
      valueGetter: (params) => {
        let displayName = params.data?.secondSupervisor?.displayName;
        if (params.data?.secondSupervisor?.defaultPhone) {
          displayName += `\xa0\xa0${params.data?.secondSupervisor?.defaultPhone?.phoneNumber}`;
        }
        return displayName;
      },
      wrapText: true,
    },
    {
      headerName: 'Sat Phone',
      field: 'satPhone',
      editable: true,
      minWidth: 225, //extra width needed to fit 2 lines of error message
      wrapText: true,
      cellEditor: CellEditorComponent,
      cellEditorParams: {
        fieldType: 'satPhoneNumber',
        placeholder: '15 Digit Number',
        // validation is coming from the Ciao field itself
      },
      cellRendererSelector: (p: ICellRendererParams) => this.getCellRenderer(p),
      cellRendererParams: { fieldType: 'text', placeholder: '15 Digit Number' },
    },
    {
      headerName: 'Sat Comm',
      field: 'satLocatorId',
      editable: true,
      minWidth: 230, //extra width needed to fit 2 lines of error message
      wrapText: true,
      cellEditor: CellEditorComponent,
      cellEditorParams: {
        fieldType: 'text',
        placeholder: 'Tracker Device Number',
        validators: [FormsUtility.isOverCharLimit],
      },
      cellRendererSelector: (p: ICellRendererParams) => this.getCellRenderer(p),
      cellRendererParams: {
        fieldType: 'text',
        placeholder: 'Tracker Device Number',
      },
    },
    {
      headerName: 'Other Notes',
      field: 'notes',
      editable: true,
      minWidth: 200,
      wrapText: true,
      cellEditor: CellEditorComponent,
      cellEditorParams: { fieldType: 'text', placeholder: 'Notes' },
      cellRendererSelector: (p: ICellRendererParams) => this.getCellRenderer(p),
      cellRendererParams: {
        fieldType: 'text',
        placeholder: 'Notes',
      },
    },
    {
      field: '',
      pinned: 'right',
      lockPinned: true,
      width: 50,
      sortable: false,
      resizable: false,
      editable: true,
      equals: (a, b) => false,
      cellEditorSelector: (p: ICellEditorParams) => ({
        component: p.node?.rowPinned ? SaveBtnComponent : RowOptionsComponent,
      }),
      cellEditorParams: (p: ICellEditorParams) => ({
        showDelete: !this.isLockedRow(p.data),
        onDelete: (rowNode: IRowNode) => {
          const data = this.rowData.slice();
          data.splice(rowNode.rowIndex, 1);
          this.subscriptions.add(
            this.tripService
              .saveTrip({ id: this.tripId, crewMembers: data })
              .pipe(
                tap((_) => {
                  this.toastr.success('Trip has been saved', 'Success');
                }),
                catchError((err) => {
                  this.showTripSaveError(err);
                  return this.tripId;
                })
              )
              .subscribe()
          );
          return data;
        },
        onSaveClick: (rowNode: IRowNode) => {
          this.savePinnedRowData();
          return;
        },
        getRowCount: () => {
          return this.rowData?.length;
        },
        getSaveAbility: () => {
          return this.allowSaveOnPinned;
        },
      }),
      cellRendererSelector: (p: ICellRendererParams) => ({
        component: p.node?.rowPinned ? SaveBtnComponent : RowOptionsComponent,
      }),
      cellRendererParams: (p: ICellRendererParams) => ({
        showDelete: !this.isLockedRow(p.data),
        onDelete: (rowNode: IRowNode) => {
          const data = this.rowData.slice();
          data.splice(rowNode.rowIndex, 1);
          this.subscriptions.add(
            this.tripService
              .saveTrip({ id: this.tripId, crewMembers: data })
              .pipe(
                tap((_) => {
                  this.toastr.success('Trip has been saved', 'Success');
                }),
                catchError((err) => {
                  this.showTripSaveError(err);
                  return this.tripId;
                })
              )
              .subscribe()
          );
          return data;
        },
        onSaveClick: (rowNode: IRowNode) => {
          this.savePinnedRowData();
          return;
        },
        getRowCount: () => {
          return this.rowData?.length;
        },
        getSaveAbility: () => {
          return this.allowSaveOnPinned;
        },
        getRequiredFields: () => {
          return ['crewMember', 'supervisor'];
        },
      }),
      singleClickEdit: false,
    },
  ];

  gridOptions: GridOptions = {
    asyncTransactionWaitMillis: 500,
    columnDefs: this.crewColDefs,
    suppressRowHoverHighlight: true,
    noRowsOverlayComponent: NoRowsMessageComponent,
    noRowsOverlayComponentParams: {
      required: true,
      tableName: 'crew member',
    },
    dataTypeDefinitions: {
      person: {
        extendsDataType: 'object',
        baseDataType: 'object',
      },
    },
    getRowId: (params) => params.data.id,
    getRowHeight: (p) => (p.node.rowPinned == 'top' ? 50 : 40),
    onCellValueChanged: (event) => {
      if (event.rowPinned || event.column.getColId() === 'rowOptions') {
        return;
      }

      this.refreshDependentLists$.next();
      console.log('Cell Value Changed');
      // Figure out if all rows are valid
      const valid = this.isTableValid();
      if (valid) {
        this.saveCrewMemberData();
      }
    },
    //allow tabbing to row options for accessability
    onCellKeyDown: (event: any) => {
      if (event.event.code === 'Enter' || event.event.code === 'NumpadEnter') {
        const isPinned = event.column.getPinned();
        if (isPinned) {
          // Prevent default and stop propagation to avoid double Enter key requirement
          event.event.preventDefault();
          event.event.stopPropagation();

          const cell = this.gridApi.getCellRendererInstances({
            rowNodes: [event.api.getRowNode(event.node?.id)],
            columns: [event.column],
          })[0];
          if (cell) {
            const renderer = cell as RowOptionsComponent;
            renderer.openMenu();
          }
        }
      }
    },
  };

  // Needed for succesfull tabbing
  onCellFocused(_event: any) {
    // reset top pinned row allows immediate focus on row options for some reason
    this.pinnedTopRowData = [this.inputRow];
  }

  ngOninit() {}

  gridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.applyReadOnly();
  }

  /** Force a column non-editable and hide its row-delete action (if any). */
  private toReadOnlyColDef(col: ColDef): ColDef {
    const next: ColDef = { ...col, editable: false };
    for (const key of ['cellRendererParams', 'cellEditorParams'] as const) {
      const params = (next as any)[key];
      if (params && typeof params === 'object' && 'showDelete' in params) {
        (next as any)[key] = { ...params, showDelete: false };
      }
    }
    return next;
  }

  /** Apply (or clear) the closed-trip read-only lock on the grid. */
  private applyReadOnly() {
    if (!this.gridApi) return;
    this.gridApi.setGridOption(
      'columnDefs',
      this._readOnly
        ? this.crewColDefs.map((c) => this.toReadOnlyColDef(c))
        : this.crewColDefs
    );
    this.pinnedTopRowData = this._readOnly ? [] : [this.inputRow];
    this.gridApi.setGridOption('pinnedTopRowData', this.pinnedTopRowData);
  }

  get gridRows() {
    const rows: IRowNode[] = [];
    this.gridApi.forEachNode((node) => {
      if (!node.rowPinned) {
        rows.push(node);
      }
    });
    return rows;
  }

  savePinnedRowData() {
    //get pinned row node
    var rowNode = this.gridApi.getPinnedTopRow(0);
    // add row
    this.gridApi.applyTransaction({ add: [rowNode?.data] });
    //reset pinned row
    this.inputRow = {};
    this.pinnedTopRowData = [this.inputRow];
    this.allowSaveOnPinned = false;
    this.saveCrewMemberData();
  }

  onCellEditingStopped(params: CellEditingStoppedEvent) {
    if (!params.rowPinned) {
      return;
    }
    this.refreshDependentLists$.next();
    // if pinned row data is filed out then attempt to add it to the list
    if (this.isRowValid(params.node)) {
      // enable/disable the save button
      this.allowSaveOnPinned = true;
      this.gridApi.refreshCells({ force: true });
    } else {
      this.allowSaveOnPinned = false;
      this.gridApi.refreshCells({ force: true });
    }
    return;
  }

  onKeyDown(event: any) {
    if (!this.allowSaveOnPinned) return;
    if (event.event.key === 'Enter' || event.event.code === 'NumpadEnter') {
      const focusedCell = this.gridApi.getFocusedCell();
      if (focusedCell.rowPinned) {
        this.savePinnedRowData();
      } else {
        this.saveCrewMemberData();
      }
    }
    return;
  }
  private showTripSaveError(err: unknown) {
    console.error(err);
    const errorBlob = JSON.stringify(
      (err as { error?: unknown })?.error ?? err ?? ''
    );
    if (/Dispatcher.*cannot be assigned.*Crew Member/i.test(errorBlob)) {
      this.toastr.error(
        'Dispatchers cannot be assigned as a Crew Member, POC, or Additional POC on a trip.',
        'Error'
      );
      return;
    }
    if (/Guests cannot be assigned.*POC/i.test(errorBlob)) {
      this.toastr.error(
        'Guests cannot be assigned as a POC or Additional POC on a trip.',
        'Error'
      );
      return;
    }
    this.toastr.error('Failed to Save Trip', 'Error');
  }

  saveCrewMemberData() {
    this.gridApi.forEachNode((node) => {
      // only take in the non-pinned nodes
      if (node.rowPinned === 'top') {
        return;
      } else {
        const temp = this.convertDataToAttr(node.data);
        // add to/update rowData
        if (temp.id) {
          //existing row item so update it
          const index = this.rowData.findIndex((row) => row.id === temp.id);
          this.rowData[index] = temp;
        } else {
          //if not add it
          this.rowData.push(temp);
        }
      }
    });
    this.subscriptions.add(
      this.tripService
        .saveTrip({ id: this.tripId, crewMembers: this.rowData })
        .pipe(
          tap(() => this.toastr.success('Successfully Saved Trip')),
          catchError((err) => {
            this.showTripSaveError(err);
            // Return Real Trip on Error
            return this.tripId;
          })
        )
        .subscribe()
    );
  }

  getCellRenderer(p) {
    const node = p.node;
    const pinnedRenderer = {
      component: PinnedCellRendererComponent,
    };
    const renderer = {
      component: CellRendererComponent,
    };
    // Person columns (Crew Member / POC / Additional POC) render through a
    // dedicated component so the guest "G" badge can sit between the name and
    // the phone number.
    const personRenderer = {
      component: PersonNameCellRendererComponent,
    };

    //show pinned renderer only when there is no values
    if (node.rowPinned === 'top') {
      return pinnedRenderer;
    }
    // For the locked guest row, show pinned renderer on empty cells so
    // placeholders still appear for POC / Sat Phone / Sat Comm / Notes.
    if (this.isLockedRow(node.data)) {
      const field = p.colDef?.field;
      const rawValue = field ? node.data?.[field] : null;
      const isEmpty =
        rawValue === null || rawValue === undefined || rawValue === '';
      if (isEmpty) {
        return pinnedRenderer;
      }
    }
    if (p.colDef?.cellDataType === 'person') {
      return personRenderer;
    }
    return renderer;
  }

  convertDataToAttr(rowData): CrewMemberMetaAttributes {
    const crewMember = {
      id: rowData?.id ?? undefined,
      crewMember: rowData?.crewMember ?? null,
      supervisor: rowData?.supervisor ?? null,
      secondSupervisor: rowData?.secondSupervisor ?? null,
      satPhone: rowData?.satPhone ?? null,
      satLocatorId: rowData?.satLocatorId ?? null,
      notes: rowData?.notes ?? null,
    };
    return crewMember;
  }

  isRowValid(row: IRowNode<CrewMemberMetaAttributes>, property?: string) {
    this.testFormGroup.reset(row.data);
    if (property) {
      // if get(property) isn't there, then this should error because we missed something
      return this.testFormGroup.get(property).valid;
    }
    return this.testFormGroup.valid;
  }

  getRowValidationErrors(
    row: IRowNode<CrewMemberMetaAttributes>,
    property?: string
  ) {
    if (property) {
      // if get(property) isn't there, then this should error because we missed something
      const validationProperty = this.testFormGroup.get(property);
      return validationProperty?.errors;
    } else {
      for (const propertyKey in row.data) {
        if (Object.hasOwn(row.data, propertyKey)) {
          const validationProperty = this.testFormGroup.get(propertyKey);

          if (validationProperty && !validationProperty.valid) {
            return validationProperty.errors;
          }
        }
      }
    }

    return null;
  }

  isTableValid() {
    if (!this.gridApi) {
      this.errors.emit(null);
      return true;
    }
    const errors: ValidationErrors[] = [];
    let everythingValid = true;

    this.gridApi.forEachNode((node) => {
      if (this.isLockedRow(node.data)) return;
      this.testFormGroup.reset(node.data);
      const rowErrors = this.getRowValidationErrors(node);
      if (rowErrors) {
        everythingValid = false;
        errors.push(rowErrors);
      }
    });

    this.errors.emit(everythingValid ? null : errors);
    return everythingValid;
  }
}
